import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { Property } from '../../types';
import { PropertyCard } from '../../components/cards/PropertyCard';
import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb, BreadcrumbItem } from '../../components/seo/Breadcrumb';
import { getLocationContent, LocationContent } from '../../data/locationContent';
import {
    generateBreadcrumbSchema,
    generateFAQSchema,
    generatePlaceSchema
} from '../../utils/seo/structuredData';
import {
    extractCityFromLocation,
    extractSuburbFromLocation,
    normalizeLocationForURL
} from '../../utils/seo/slugs';
import { MapPin, GraduationCap, Bus, Briefcase, Home as HomeIcon } from 'lucide-react';

export const LocationPage: React.FC = () => {
    const { city, suburb } = useParams<{ city: string; suburb?: string }>();
    const { properties } = useData();
    const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
    const [locationInfo, setLocationInfo] = useState<LocationContent | null>(null);

    useEffect(() => {
        if (!city) return;

        // Get location content from database
        const cityInfo = getLocationContent(city);
        setLocationInfo(cityInfo);

        // Filter properties by location
        const filtered = properties.filter(prop => {
            const propCity = extractCityFromLocation(prop.location);
            const propCitySlug = normalizeLocationForURL(propCity);

            // Match city
            if (propCitySlug !== city.toLowerCase()) return false;

            // If suburb specified, match suburb too
            if (suburb) {
                const propSuburb = extractSuburbFromLocation(prop.location);
                const propSuburbSlug = normalizeLocationForURL(propSuburb);
                return propSuburbSlug === suburb.toLowerCase();
            }

            return true;
        });

        setFilteredProperties(filtered.filter(p => p.status === 'AVAILABLE'));
    }, [city, suburb, properties]);

    // Build breadcrumbs
    const breadcrumbItems: BreadcrumbItem[] = [
        { name: 'Rooms', url: '/browse' },
        { name: locationInfo?.name || city?.replace(/-/g, ' ') || '', url: `/rooms/${city}` },
    ];

    if (suburb) {
        breadcrumbItems.push({
            name: suburb.replace(/-/g, ' '),
            url: `/rooms/${city}/${suburb}`
        });
    }

    // Generate schemas
    const breadcrumbSchema = generateBreadcrumbSchema([
        { name: 'Home', url: 'https://rooms.indigobeans.co.za/' },
        ...breadcrumbItems.map(item => ({
            name: item.name,
            url: `https://rooms.indigobeans.co.za${item.url}`
        }))
    ]);

    const faqSchema = locationInfo?.faqs
        ? generateFAQSchema(locationInfo.faqs)
        : undefined;

    const placeSchema = locationInfo
        ? generatePlaceSchema(
            locationInfo.name,
            locationInfo.description,
            locationInfo.latitude,
            locationInfo.longitude,
            locationInfo.province
        )
        : undefined;

    const schemas = [breadcrumbSchema, faqSchema, placeSchema].filter(Boolean);

    // Generate page title and description
    const pageTitle = suburb
        ? `Rooms for Rent in ${suburb.replace(/-/g, ' ')}, ${locationInfo?.name || city?.replace(/-/g, ' ')} | IndigoBeans Rooms`
        : `Rooms for Rent in ${locationInfo?.name || city?.replace(/-/g, ' ')} | IndigoBeans Rooms`;

    const pageDescription = suburb
        ? `Find affordable rooms for rent in ${suburb.replace(/-/g, ' ')}, ${locationInfo?.name || city?.replace(/-/g, ' ')}. Browse verified student accommodation and rooms near universities and transport.`
        : locationInfo?.metaDescription || `Find affordable rooms for rent in ${city?.replace(/-/g, ' ')}. Browse verified accommodation options near universities, transport, and business hubs.`;

    if (!city) {
        return <div className="min-h-screen flex items-center justify-center">Invalid location</div>;
    }

    return (
        <>
            <SEOHead
                title={pageTitle}
                description={pageDescription}
                keywords={[
                    `rooms for rent ${city.replace(/-/g, ' ')}`,
                    `student accommodation ${city.replace(/-/g, ' ')}`,
                    `affordable rooms ${city.replace(/-/g, ' ')}`,
                    `rooms near me`,
                    locationInfo?.province || 'South Africa'
                ]}
                canonical={`https://rooms.indigobeans.co.za/rooms/${city}${suburb ? `/${suburb}` : ''}`}
                schema={schemas as object[]}
            />

            <div className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-[#2f5c96] to-[#1e3a5f] text-white py-12">
                    <div className="max-w-7xl mx-auto px-4">
                        <Breadcrumb items={breadcrumbItems} className="mb-4 text-white/80" />

                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            Rooms for Rent in {suburb ? `${suburb.replace(/-/g, ' ')}, ` : ''}{locationInfo?.name || city.replace(/-/g, ' ')}
                        </h1>

                        {locationInfo && (
                            <p className="text-lg md:text-xl text-white/90 max-w-3xl">
                                {locationInfo.description}
                            </p>
                        )}

                        <div className="mt-6 flex items-center gap-6 text-sm">
                            <span className="flex items-center gap-2">
                                <HomeIcon className="w-5 h-5" />
                                {filteredProperties.length} rooms available
                            </span>
                            {locationInfo && (
                                <span className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5" />
                                    {locationInfo.province}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Listings Column */}
                        <div className="lg:col-span-2">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                Available Rooms ({filteredProperties.length})
                            </h2>

                            {filteredProperties.length > 0 ? (
                                <div className="grid md:grid-cols-2 gap-6">
                                    {filteredProperties.map(property => (
                                        <PropertyCard key={property.id} property={property} />
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg shadow p-8 text-center">
                                    <p className="text-gray-600 mb-4">
                                        No rooms currently available in this area.
                                    </p>
                                    <Link
                                        to="/browse"
                                        className="text-[#2f5c96] hover:underline font-medium"
                                    >
                                        Browse all available rooms →
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Info Sidebar */}
                        {locationInfo && (
                            <div className="space-y-6">
                                {/* Universities */}
                                {locationInfo.universities.length > 0 && (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                                            <GraduationCap className="w-5 h-5 text-[#2f5c96]" />
                                            Universities & Colleges
                                        </h3>
                                        <ul className="space-y-2">
                                            {locationInfo.universities.map((uni, idx) => (
                                                <li key={idx} className="text-gray-700 text-sm">• {uni}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Transport */}
                                {locationInfo.transport.length > 0 && (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                                            <Bus className="w-5 h-5 text-[#2f5c96]" />
                                            Public Transport
                                        </h3>
                                        <ul className="space-y-2">
                                            {locationInfo.transport.map((mode, idx) => (
                                                <li key={idx} className="text-gray-700 text-sm">• {mode}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Business Hubs */}
                                {locationInfo.businessHubs.length > 0 && (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                                            <Briefcase className="w-5 h-5 text-[#2f5c96]" />
                                            Business Hubs
                                        </h3>
                                        <ul className="space-y-2">
                                            {locationInfo.businessHubs.map((hub, idx) => (
                                                <li key={idx} className="text-gray-700 text-sm">• {hub}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Nearby Suburbs */}
                                {!suburb && locationInfo.nearbySuburbs && locationInfo.nearbySuburbs.length > 0 && (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                                            <MapPin className="w-5 h-5 text-[#2f5c96]" />
                                            Popular Suburbs
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {locationInfo.nearbySuburbs.map((sub, idx) => (
                                                <Link
                                                    key={idx}
                                                    to={`/rooms/${city}/${normalizeLocationForURL(sub)}`}
                                                    className="px-3 py-1 bg-gray-100 hover:bg-[#2f5c96] hover:text-white rounded-full text-sm transition-colors"
                                                >
                                                    {sub}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* FAQ Section */}
                    {locationInfo?.faqs && locationInfo.faqs.length > 0 && (
                        <div className="mt-12">
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Frequently Asked Questions
                            </h2>
                            <div className="bg-white rounded-lg shadow divide-y">
                                {locationInfo.faqs.map((faq, idx) => (
                                    <div key={idx} className="p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                            {faq.question}
                                        </h3>
                                        <p className="text-gray-700">
                                            {faq.answer}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
